import React, { useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './CircularLoader.css';

const DEFAULT_ACCENT = '#623C91';

const CircularLoader = ({
  text = 'Loading Displays...',
  accentColor,
  uppercase = true,
}) => {
  const uid = useId().replace(/:/g, '');
  const gradId = `loaderGradient-${uid}`;
  const accent =
    accentColor && String(accentColor).trim()
      ? String(accentColor).trim()
      : DEFAULT_ACCENT;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="circular-loader-container"
        style={{ '--loader-accent': accent }}
      >
        <div className="creative-loader-wrapper">
          <div className="loader-logo-wrapper">
            <motion.div
              className="loader-logo"
              animate={{
                rotate: [0, 360],
                scale: [1, 1.1, 1],
              }}
              transition={{
                rotate: {
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'linear',
                },
                scale: {
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                },
              }}
            >
              <svg
                width="80"
                height="80"
                viewBox="0 0 100 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient
                    id={gradId}
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor={accent} stopOpacity="1" />
                    <stop offset="50%" stopColor={accent} stopOpacity="0.75" />
                    <stop offset="100%" stopColor={accent} stopOpacity="1" />
                  </linearGradient>
                </defs>

                <motion.circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke={`url(#${gradId})`}
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray="267 47"
                  animate={{
                    strokeDashoffset: [0, 314],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                />
                <motion.circle
                  cx="50"
                  cy="50"
                  r="30"
                  fill={`url(#${gradId})`}
                  animate={{
                    scale: [1, 1.15, 1],
                    opacity: [0.4, 0.7, 0.4],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              </svg>
            </motion.div>
          </div>

          <div className="loader-dots">
            {[0, 1, 2].map((index) => (
              <motion.div
                key={index}
                className="loader-dot"
                animate={{
                  y: [0, -10, 0],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  delay: index * 0.15,
                  ease: 'easeInOut',
                }}
              />
            ))}
          </div>

          <motion.div
            className={`loader-text ${
              uppercase ? 'loader-text--caps' : 'loader-text--sentence'
            }`}
            style={{ color: accent }}
            animate={{
              opacity: [0.6, 1, 0.6],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            {text}
          </motion.div>

          <div className="loader-progress-container">
            <motion.div
              className="loader-progress-bar"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CircularLoader;
